import React, { useEffect, useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import * as apicall from "../../components/Apicall";
import AssetFieldsForm from "../../components/CustomerAssets/AssetFieldsForm";

/**
 * "Service Specifics" section for the booking screen - the real,
 * industry-configurable replacement for the old dead "Car Details" demo
 * block (carMake/carModel/speedometerReading + Customer complaints/
 * Completed action, never wired to save). Deliberately a co-located
 * sibling file to Bookings.jsx, matching PartsUsedSection.jsx/
 * StagedPartsSection.jsx's existing convention of not growing that
 * 4600+ line file further.
 *
 * Renders nothing if the vendor's industry has no seeded asset-type schema
 * (see ezp_customer_assets_seed_data.sql). Lets the user pick one of the
 * customer's existing assets (e.g. one of their cars) or add a new one
 * inline, plus a single editable "customer complaint/action taken" note.
 * Every industry - Car Service vehicles included - uses this same generic
 * ezp_customer_assets flow; there is no industry-specific branch.
 *
 * "Add New" works even before a customer is known (a brand-new customer
 * typed directly on the booking form has no id yet) - in that case there's
 * nothing to save against yet, so the fields are staged as `new_asset_draft`
 * instead of calling the create-asset API immediately. Lifts
 * {customer_asset_id, new_asset_draft, new_asset_error, customer_complaint,
 * action_taken} up via onChange so Bookings.jsx can fold it into the save
 * payload - persistence happens in Bookings.jsx (existing booking, customer
 * already known) or Checkout.jsx (new booking - the draft asset, if any, is
 * created for real only once the booking creation response resolves a
 * customer_id). `new_asset_error` is non-empty when a staged draft is
 * missing required fields, so Bookings.jsx can block the save up front.
 */
function ServiceSpecificsSection({ customerId, bookingId, onChange, onLoaded }) {
  const [schema, setSchema] = useState({ asset_type: null, fields: [] });
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [newAssetForm, setNewAssetForm] = useState({ label: "", field_values: {}, custom_fields: [] });
  const [assetError, setAssetError] = useState("");
  const [savingAsset, setSavingAsset] = useState(false);
  const [complaint, setComplaint] = useState("");
  const [actionTaken, setActionTaken] = useState("");

  // Schema is platform-wide per industry - fetch once regardless of customer.
  useEffect(() => {
    (async () => {
      try {
        const res = await apicall.get("ezp_customer_asset_schema");
        if (res?.success) setSchema(res.data);
      } catch (e) { console.log(e); }
    })();
  }, []);

  // Existing booking: prefill the previously-saved note + asset selection.
  useEffect(() => {
    if (!bookingId) return;
    (async () => {
      try {
        const res = await apicall.get(`ezp_booking_service_details/${bookingId}`);
        if (res?.success) {
          if (res.data) {
            setSelectedAssetId(res.data.customer_asset_id || "");
            setComplaint(res.data.customer_complaint || "");
            setActionTaken(res.data.action_taken || "");
          }
          // Hands the saved values to the parent as its "unchanged" baseline
          // (null data = nothing saved yet = empty baseline).
          onLoaded?.(res.data || null);
        }
      } catch (e) { console.log(e); }
    })();
  }, [bookingId]);

  const fetchAssets = async () => {
    if (!customerId) { setAssets([]); return; }
    try {
      const res = await apicall.get(`ezp_customer_assets/${customerId}`);
      if (res?.success) setAssets(res.data || []);
    } catch (e) { console.log(e); }
  };
  useEffect(() => { fetchAssets(); }, [customerId]);

  // Labels of required fields still blank in the "Add New" form.
  const missingRequired = (schema.fields || [])
    .filter((f) => f.is_required && !String(newAssetForm.field_values?.[f.field_key] ?? "").trim())
    .map((f) => f.field_label);

  // No customer resolved yet (brand-new customer being typed in directly on
  // the booking form) - can't create the asset row yet since it needs a
  // real customer_id, so stage it as a draft instead. Lifted up whenever it
  // has actual content; Checkout.jsx creates it for real once the booking
  // (and therefore the new customer) is created and a customer_id exists.
  const isDraftMode = showAddAsset && !customerId;
  const draftHasContent =
    !!newAssetForm.label ||
    Object.values(newAssetForm.field_values || {}).some(Boolean) ||
    (newAssetForm.custom_fields || []).some((cf) => cf.key);
  const newAssetError = isDraftMode && draftHasContent && missingRequired.length > 0
    ? `${missingRequired.join(", ")} required`
    : "";

  // Lift state up on every change.
  useEffect(() => {
    onChange?.({
      customer_asset_id: selectedAssetId || null,
      new_asset_draft: isDraftMode && draftHasContent
        ? { asset_type_id: schema.asset_type?.id, ...newAssetForm }
        : null,
      new_asset_error: newAssetError,
      customer_complaint: complaint,
      action_taken: actionTaken,
    });
  }, [selectedAssetId, complaint, actionTaken, isDraftMode, draftHasContent, newAssetForm, newAssetError, schema.asset_type, onChange]);

  const saveNewAsset = async () => {
    if (!customerId || !schema.asset_type) return;
    if (missingRequired.length > 0) {
      setAssetError(`${missingRequired.join(", ")} required`);
      return;
    }
    setAssetError("");
    try {
      setSavingAsset(true);
      const customFieldsObj = {};
      (newAssetForm.custom_fields || []).forEach((cf) => { if (cf.key) customFieldsObj[cf.key] = cf.value; });
      const res = await apicall.post({
        customer_id: customerId,
        asset_type_id: schema.asset_type.id,
        label: newAssetForm.label,
        field_values: newAssetForm.field_values,
        custom_fields: customFieldsObj,
      }, "ezp_customer_assets");
      if (res?.success) {
        await fetchAssets();
        setSelectedAssetId(res.data.id);
        setShowAddAsset(false);
        setNewAssetForm({ label: "", field_values: {}, custom_fields: [] });
      } else {
        setAssetError(res?.errors?.[0]?.msg || res?.message || `Could not save ${schema.asset_type.label.toLowerCase()}.`);
      }
    } catch (e) { console.log(e); setAssetError(`Could not save ${schema.asset_type.label.toLowerCase()}.`); }
    finally { setSavingAsset(false); }
  };

  if (!schema.asset_type) return null;
  const typeLabel = schema.asset_type.label;

  return (
    <div className="mb-4">
      <h6 className="section-title">Service Specifics</h6>

      <Row>
        <Col md={8} className="mb-2">
          <Form.Group>
            <Form.Label>Select {typeLabel}:</Form.Label>
            <Form.Select
              value={selectedAssetId}
              onChange={(e) => setSelectedAssetId(e.target.value)}
              disabled={!customerId}
              className="form-input"
              style={{ height: "40px", backgroundColor: "white", border: "1px solid #E9ECEF" }}
            >
              <option value="">{customerId ? `Select ${typeLabel}` : "Select a customer first"}</option>
              {assets.map((a, idx) => (
                <option key={a.id} value={a.id}>{a.label || `${typeLabel} ${idx + 1}`}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} className="mb-2 d-flex align-items-end">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => { setShowAddAsset((s) => !s); setAssetError(""); }}
          >
            {showAddAsset ? "Cancel" : `+ Add New ${typeLabel}`}
          </Button>
        </Col>
      </Row>

      {showAddAsset && (
        <div style={{ border: "1px solid #E9ECEF", borderRadius: 8, padding: 16, marginBottom: 16, background: "#fafbfc" }}>
          {isDraftMode && (
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
              This will be saved to the new customer's profile once the booking is created.
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 5 }}>
              Label (optional, e.g. "My Toyota" - filled in from the details if left blank)
            </label>
            <Form.Control
              type="text"
              value={newAssetForm.label}
              onChange={(e) => setNewAssetForm((f) => ({ ...f, label: e.target.value }))}
            />
          </div>
          <AssetFieldsForm
            fieldDefs={schema.fields}
            values={newAssetForm.field_values}
            onChange={(key, val) => setNewAssetForm((f) => ({ ...f, field_values: { ...f.field_values, [key]: val } }))}
            customFields={newAssetForm.custom_fields}
            onCustomFieldsChange={(next) => setNewAssetForm((f) => ({ ...f, custom_fields: next }))}
          />
          {assetError && <div style={{ fontSize: 12, color: "#dc2626", marginBottom: 8 }}>{assetError}</div>}
          {!isDraftMode && (
            <Button size="sm" onClick={saveNewAsset} disabled={savingAsset}>
              {savingAsset ? "Saving…" : `Save ${typeLabel}`}
            </Button>
          )}
        </div>
      )}

      <Row>
        <Col md={6} className="mb-2">
          <Form.Group>
            <Form.Label>Customer complaint / reason:</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={complaint}
              onChange={(e) => setComplaint(e.target.value)}
              className="form-input"
            />
          </Form.Group>
        </Col>
        <Col md={6} className="mb-2">
          <Form.Group>
            <Form.Label>Action taken:</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              className="form-input"
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
}

export default ServiceSpecificsSection;
